/**
 * \u6e38\u620f\u5f15\u64ce \u2014\u2014 \u534f\u8c03 S1/S2/S3/S5/S6\uff0c\u5bf9\u5916\u66b4\u9732\u4e00\u7ec4\u610f\u56fe\u63a5\u53e3\u3002
 *
 * \u94c1\u5f8b\uff1a\u5f15\u64ce\u4e0d\u77e5\u9053\u6e32\u67d3\u4e0e\u8f93\u5165\u7684\u5b58\u5728\uff0c\u53ea\u53d1\u4e8b\u4ef6\u3002
 * \u8f93\u5165\u9501\u5b9a\uff1a\u53ea\u6709 BoardStable \u540e isBusy \u624d\u4e3a false\u3002
 */

import type { Board0, Coord, PieceColor, SpecialType, MatchGroup, Direction } from './types';
import { boardToString, generateBoard, isSwapLegal, swapCells, findMatchGroups, getCell } from './board';
import { settle, type FallRecord } from './cascade';
import { computeSpawnCell, computeDetonation, placeSpecial, pickHighestShape, shapeToSpecial } from './special';
import { computeMoveScore, leftoverScore, evalStars, type MoveScoreInput } from './score';
import { getLevel, validateLevel, type LevelData } from './level';
import { EventBus } from './bus';

export interface GameSnapshot {
  board: Board0;
  level: LevelData;
  movesLeft: number;
  score: number;
  goalProgress: Map<PieceColor, number>;
  busy: boolean;
  finished: 'playing' | 'won' | 'lost';
  stars: 1 | 2 | 3;
}

/** \u88ab\u6d88\u9664\u7684\u5355\u683c\uff08\u5e26\u989c\u8272\uff0c\u4f9b\u788e\u88c2\u7279\u6548\u7528\uff09 */
export interface ClearedCell {
  cell: Coord;
  color: PieceColor;
  special?: SpecialType;
}

/** \u7279\u6b8a\u5757\u751f\u6210\u8bb0\u5f55 */
export interface SpawnRecord {
  type: SpecialType;
  cell: Coord;
  color?: PieceColor;
}

/** \u4e00\u4e2a\u8fde\u9501\u7ea7\u7684\u52a8\u4f5c\uff08\u6d88\u9664 + \u4e0b\u843d\uff09 */
export interface MoveLevelRecord {
  level: number;
  cleared: ClearedCell[];
  falls: FallRecord[];
}

/** \u4e00\u6574\u6b65\u7684\u89c6\u89c9\u65e5\u5fd7\uff08\u4f9b\u6e32\u67d3\u5c42\u6309\u65f6\u95f4\u8f74\u64ad\u653e\uff09 */
export interface MoveRecord {
  from: Coord;
  to: Coord;
  /** \u4ea4\u6362\u662f\u5426\u4e3a\u6709\u6548\u4ea4\u6362\uff08\u5426\u5219\u4e3a\u56de\u5f39\uff09 */
  swapped: boolean;
  /** \u4e3b\u52a8\u5f15\u7206\u4e8b\u4ef6 */
  detonations: { type: SpecialType; cell: Coord; cells: ClearedCell[] }[];
  /** \u5404\u7ea7\u52a8\u4f5c */
  records: MoveLevelRecord[];
  /** \u672c\u6b65\u751f\u6210\u7684\u7279\u6b8a\u5757 */
  spawns: SpawnRecord[];
  /** \u672c\u6b65\u5f97\u5206 */
  gainedScore?: number;
  /** \u672c\u6b65\u662f\u5426\u89e6\u53d1\u91cd\u6392 */
  reshuffled?: boolean;
}

export class Game {
  readonly bus = new EventBus();
  private level: LevelData;
  private board: Board0;
  private movesLeft: number;
  private score = 0;
  private goalProgress = new Map<PieceColor, number>();
  private busy = false;
  private finished: 'playing' | 'won' | 'lost' = 'playing';
  private stars: 1 | 2 | 3 = 1;
  private rand: () => number;
  /** \u6700\u8fd1\u4e00\u6b65\u7684\u89c6\u89c9\u65e5\u5fd7\uff08\u6d88\u8d39\u540e\u7f6e null\uff09 */
  private lastMove: MoveRecord | null = null;

  constructor(levelId: number, rand: () => number = Math.random) {
    const lv = getLevel(levelId);
    if (!lv) throw new Error(`\u5173\u5361 ${levelId} \u4e0d\u5b58\u5728`);
    const v = validateLevel(lv);
    if (!v.ok) {
      this.bus.emit('LevelDataRejected', { levelId, reason: v.reason });
      throw new Error(`\u5173\u5361\u6570\u636e\u4e0d\u5408\u6cd5\uff1a${v.reason}`);
    }
    this.level = lv;
    this.rand = rand;
    this.board = generateBoard(rand);
    this.movesLeft = lv.moves;
    for (const g of lv.goals) this.goalProgress.set(g.color, 0);

    this.bus.emit('LevelStarted', {
      levelId: lv.levelId,
      goals: lv.goals,
      moves: lv.moves,
    });
  }

  /** \u8bfb\u53d6\u5f53\u524d\u72b6\u6001\uff08\u6e32\u67d3\u5c42\u63e1\u4f4f\u8fd9\u4e2a\uff09 */
  snapshot(): GameSnapshot {
    return {
      board: this.board,
      level: this.level,
      movesLeft: this.movesLeft,
      score: this.score,
      goalProgress: this.goalProgress,
      busy: this.busy,
      finished: this.finished,
      stars: this.stars,
    };
  }

  isBusy(): boolean { return this.busy; }

  /**
   * S8 \u4ea4\u4e92\u5165\u53e3\uff1a\u73a9\u5bb6\u53d1\u8d77\u4e00\u6b21\u4ea4\u6362\u610f\u56fe\u3002
   * \u82e5\u4e0d\u5408\u6cd5\uff0c\u56de\u5f39\u4e14\u4e0d\u6d88\u8017\u6b65\u6570\uff08D2\uff09\u3002
   */
  /**
   * S8 \u4ea4\u4e92\u5165\u53e3\uff1a\u73a9\u5bb6\u53d1\u8d77\u4e00\u6b21\u4ea4\u6362\u610f\u56fe\u3002
   * \u82e5\u4e0d\u5408\u6cd5\uff0c\u56de\u5f39\u4e14\u4e0d\u6d88\u8017\u6b65\u6570\uff08D2\uff09\u3002
   *
   * \u91cd\u8981\uff1a\u672c\u51fd\u6570\u4f1a\u5728 this.lastMove \u91cc\u8bb0\u5f55\u6574\u6b65\u7684\u89c6\u89c9\u65e5\u5fd7\uff0c
   * \u4f9b\u6e32\u67d3\u5c42\u6309\u65f6\u95f4\u8f74\u64ad\u653e\u3002\u903b\u8f91\u4ecd\u7136\u662f\u540c\u6b65\u7acb\u5373\u7b97\u5b8c\u7684\u3002
   */
  trySwap(from: Coord, to: Coord): boolean {
    if (this.busy || this.finished !== 'playing') return false;

    if (!isSwapLegal(this.board, from, to)) {
      this.bus.emit('SwapRejected', { from, to });
      return false;
    }

    const a = getCell(this.board, from);
    const b = getCell(this.board, to);

    swapCells(this.board, from, to);

    // \u5224\u5b9a\u662f\u5426\u4e3a\u300c\u5373\u5f15\u7206\u4ea4\u6362\u300d\uff08FC-1\uff09
    const specialAtFrom = a?.special ? { cell: from, type: a.special } : null;
    const specialAtTo = b?.special ? { cell: to, type: b.special } : null;

    this.movesLeft -= 1;
    this.busy = true;

    const spawnInputs: MoveScoreInput['spawns'] = [];
    const detonationInputs: MoveScoreInput['detonations'] = [];
    const groupsByLevel: MoveScoreInput['groupsByLevel'] = [];

    // \u89c6\u89c9\u65e5\u5fd7\uff1a\u6b64\u6b21\u4ea4\u6362\u662f\u5426\u6210\u7acb
    const record: MoveRecord = {
      from, to,
      swapped: true,
      detonations: [],
      records: [],
      spawns: [],
    };

    // \u2500\u2500 \u5148\u5904\u7406\u4e3b\u52a8\u5f15\u7206\uff08\u82e5\u6709\uff09
    const detoList = [specialAtFrom, specialAtTo].filter(Boolean) as { cell: Coord; type: SpecialType }[];
    if (detoList.length > 0) {
      // \u672c\u8f6e\u4e0d\u5f00\u653e\u7ec4\u5408\u5f15\u7206\uff1a\u4e24\u7279\u6b8a\u5757\u4ea4\u6362\u4ec5 from \u683c\u90a3\u679a\u5f15\u7206
      const chosen = detoList[0];
      const dir: Direction = to.c !== from.c ? (to.c > from.c ? 'right' : 'left') : (to.r > from.r ? 'down' : 'up');
      const result = computeDetonation(this.board, { source: chosen.cell, from, direction: dir, targetColor: b?.color });

      // \u8bb0\u5f55\u88ab\u5f15\u7206\u683c\u7684\u989c\u8272\uff0c\u4f9b\u788e\u88c2\u7279\u6548
      const detoCleared: ClearedCell[] = [];
      for (const p of result.clearedCells) {
        const t = getCell(this.board, p);
        if (t?.color) detoCleared.push({ cell: p, color: t.color, special: t.special });
      }
      record.detonations.push({ type: chosen.type, cell: chosen.cell, cells: detoCleared });

      this.clearCells(result.clearedCells);
      detonationInputs.push({ type: chosen.type, cellCount: result.clearedCells.length, level: 1 });
      this.bus.emit('SpecialDetonated', {
        type: chosen.type,
        cells: result.clearedCells,
        level: 1,
      });
    } else {
      // \u666e\u901a\u4ea4\u6362\uff1a\u5148\u770b\u80fd\u4e0d\u80fd\u6210\u5339\u914d
      const groups = findMatchGroups(this.board);
      if (groups.length === 0) {
        // \u4e0d\u6210\u5339\u914d \u2014\u2014 \u56de\u5f39\uff0c\u8fd8\u539f\u6b65\u6570
        swapCells(this.board, from, to);
        this.movesLeft += 1;
        this.busy = false;
        record.swapped = false;
        this.bus.emit('SwapRejected', { from, to });
        return false;
      }
      this.bus.emit('MatchFormed', { groups });
      groupsByLevel.push({ level: 1, groups });

      // \u5148\u8bb0\u5f55\u672c\u6b21\u6d88\u9664\u7684\u683c\uff08\u542b\u989c\u8272\uff09
      const cleared: ClearedCell[] = [];
      for (const g of groups) {
        for (const p of g.cells) {
          const t = getCell(this.board, p);
          if (t?.color) cleared.push({ cell: p, color: t.color, special: t.special });
        }
      }

      // \u5904\u7406\u7279\u6b8a\u5757\u751f\u6210\uff08\u672c\u5207\u7247\u4ec5\u63a8\u8fdb\u5668\uff0c\u4f46\u63a5\u53e3\u901a\u7528\uff09
      const spawns = this.handleSpawns(groups, 'swap', from, spawnInputs);
      record.spawns.push(...spawns);

      // \u5148\u6e05\u7a7a\u6d88\u9664\u683c\uff0c\u518d\u628a\u7279\u6b8a\u5757\u653e\u56de\u53bb\uff08\u4fdd\u8bc1\u7279\u6b8a\u5757\u4e0d\u88ab\u8bef\u6e05\uff09
      this.clearCells(groups.flatMap((g) => g.cells));
      for (const sp of spawns) {
        placeSpecial(this.board, sp.cell, sp.type, sp.color);
      }

      record.records.push({
        level: 1,
        cleared,
        falls: [],
      });
    }

    // \u2500\u2500 \u8de8\u56de\u5408\u8fde\u9501
    // forceGravityFirst\uff1a\u7b2c\u4e00\u6ce2\u5df2\u7531\u4e0a\u9762\u624b\u52a8\u6d88\u9664\uff0c\u8fd9\u91cc\u5148\u843d\u5b9e\u76d8\u9762\uff08\u91cd\u529b+\u8865\u6599\uff09\u518d\u8fdb\u5165\u8fde\u9501\u68c0\u6d4b
    const cascade = settle(this.board, this.bus, this.rand, { forceGravityFirst: true });
    groupsByLevel.push(...cascade.groupsByLevel);

    // \u8fde\u9501\u4e2d\u7684\u7279\u6b8a\u5757\u751f\u6210\uff08\u7b2c 2 \u7ea7\u8d77\uff09
    for (const { level, groups } of cascade.groupsByLevel) {
      const spawns = this.handleSpawns(groups, 'cascade', undefined, spawnInputs, level);
      record.spawns.push(...spawns);
      // \u7279\u6b8a\u5757\u843d\u4f4d\u540e\uff0c\u540e\u7eed\u5df2\u7531 settle \u5904\u7406\u6389\u843d
    }

    // \u628a cascade \u7684\u52a8\u4f5c\u8bb0\u5f55\u5e76\u5165\u672c\u6b65\u65e5\u5fd7
    // \u82e5\u7b2c 1 \u7ea7\u5df2\u5728\u524d\u9762\u5f55\u5165\uff08\u666e\u901a\u4ea4\u6362\uff09\uff0ccascade \u7ea7\u53f7\u6574\u4f53 +1
    const levelOffset = record.records.length > 0 ? 1 : 0;
    for (const r of cascade.records) {
      record.records.push({
        level: r.level + levelOffset,
        cleared: r.cleared,
        falls: r.falls,
      });
    }
    record.reshuffled = cascade.reshuffled;

    // \u2500\u2500 \u8ba1\u5206
    const res = computeMoveScore({ groupsByLevel, spawns: spawnInputs, detonations: detonationInputs });
    this.score += res.score;
    record.gainedScore = res.score;

    // \u2500\u2500 \u76ee\u6807\u8fdb\u5ea6\uff08\u7edf\u8ba1\u5df2\u6d88\u9664\u7684\u76ee\u6807\u8272\uff09
    this.updateGoals(groupsByLevel);

    this.lastMove = record;
    this.busy = false;
    this.bus.emit('BoardStable', { finalCascadeLevel: cascade.finalCascadeLevel });
    this.checkEnd();
    return true;
  }

  /** \u53d6\u6700\u8fd1\u4e00\u6b65\u7684\u89c6\u89c9\u65e5\u5fd7\uff08\u4f9b\u6e32\u67d3\u5c42\u64ad\u653e\u540e\u53ef\u6e05\u9664\uff09 */
  takeLastMove(): MoveRecord | null {
    const m = this.lastMove;
    this.lastMove = null;
    return m;
  }

  private handleSpawns(
    groups: MatchGroup[],
    trigger: 'swap' | 'cascade',
    swapFrom: Coord | undefined,
    out: MoveScoreInput['spawns'],
    level = 1,
  ): SpawnRecord[] {
    const result: SpawnRecord[] = [];
    for (const g of groups) {
      const shape = pickHighestShape(g.shapeHints);
      if (!shape) continue;
      const type = shapeToSpecial(shape);
      if (!type) continue;
      const cell = computeSpawnCell(g, trigger, swapFrom);
      // \u5148\u6e05\u7a7a\u8be5\u683c\uff08\u53ef\u80fd\u662f\u5373\u5c06\u88ab\u6d88\u9664\u7684\u683c\uff09\u518d\u653e\u7f6e
      const target = this.board.cells[cell.r]?.[cell.c];
      if (target && target.kind === 'OBSTACLE') continue;
      const ok = placeSpecial(this.board, cell, type, g.color);
      if (!ok) continue;
      out.push({ type, level });
      result.push({ type, cell, color: g.color });
      this.bus.emit('SpecialGenerated', { type, cell, color: g.color });
    }
    return result;
  }

  private clearCells(cells: Coord[]): void {
    for (const p of cells) {
      const cell = getCell(this.board, p);
      if (cell && (cell.kind === 'PIECE' || cell.kind === 'SPECIAL')) {
        this.board.cells[p.r][p.c] = { kind: 'EMPTY' };
      }
    }
  }

  private updateGoals(groupsByLevel: { level: number; groups: MatchGroup[] }[]): void {
    for (const g of this.level.goals) {
      let total = this.goalProgress.get(g.color) ?? 0;
      for (const { groups } of groupsByLevel) {
        for (const grp of groups) {
          if (grp.color === g.color) total += grp.cells.length;
        }
      }
      this.goalProgress.set(g.color, total);
      this.bus.emit('GoalProgress', { targetColor: g.color, current: total, goal: g.count });
    }
  }

  private checkEnd(): void {
    const allDone = this.level.goals.every(
      (g) => (this.goalProgress.get(g.color) ?? 0) >= g.count,
    );
    if (allDone) {
      this.finished = 'won';
      this.score += leftoverScore(this.movesLeft);
      this.stars = evalStars(this.score, this.level.starLine2, this.level.starLine3);
      this.bus.emit('LevelComplete', {
        levelId: this.level.levelId,
        stars: this.stars,
        score: this.score,
        movesLeft: this.movesLeft,
      });
      return;
    }
    if (this.movesLeft <= 0) {
      this.finished = 'lost';
      this.bus.emit('LevelFailed', {
        levelId: this.level.levelId,
        score: this.score,
        best: this.score,
      });
    }
  }

  /** \u91cd\u5f00\u672c\u5173 */
  restart(): void {
    this.board = generateBoard(this.rand);
    this.movesLeft = this.level.moves;
    this.score = 0;
    this.goalProgress.clear();
    for (const g of this.level.goals) this.goalProgress.set(g.color, 0);
    this.finished = 'playing';
    this.busy = false;
    this.bus.emit('LevelStarted', { levelId: this.level.levelId, goals: this.level.goals, moves: this.level.moves });
  }

  debugBoard(): string { return boardToString(this.board); }
}
